/**
 * Per-school module access — the PURE half (no React, no network).
 *
 * `GET /me/entitlements` says which catalogue modules the caller's school(s)
 * have switched on. This file turns that response into one small object every
 * surface asks the same question of (`hooks/use-module-access.ts` wraps it in
 * react-query). Nothing here decides *roles*: `teaser` arrives from the API
 * and is already true only for ADMIN / PRINCIPAL / CHAIRMAN / DIRECTOR.
 *
 * Three outcomes for a module, in one vocabulary used by the sidebar, the
 * dock, the More sheet, dashboards, quick actions, notification links and the
 * route guard:
 *   - "visible"  the module is on (or we cannot tell yet, or it is not a
 *                switchable module at all: core paths are always visible);
 *   - "locked"   off, and the caller may see an "Upgrade" teaser in its place;
 *   - "hidden"   off, and the caller must not see it anywhere.
 *
 * Unknown never hides: while the first response is loading (or the request
 * failed) everything is "visible". The API gate is the real boundary, this is
 * the courtesy layer, and flashing a "not in your plan" screen at someone
 * whose plan is fine is worse than showing a card that 403s once.
 *
 * Launch-readiness (`lib/launch-gating.ts`) is a DIFFERENT axis: a product-wide
 * "this module is not finished yet" list. It is deliberately not folded in
 * here; callers that need both use `isPathShown()` below.
 */
import { moduleKeyForWebPath } from "@campushubai/shared";
import { isLaunchReadyPath } from "@/lib/launch-gating";

export interface ModuleStateWire {
  key: string;
  enabled: boolean;
  teaser: boolean;
}

/** `GET /me/entitlements` `data`. Everything past `modules` is optional so an older API still parses. */
export interface EntitlementsResponse {
  scope?: "school" | "network" | "none";
  schoolId?: string | null;
  plan: string | null;
  planId?: string | null;
  planName?: string | null;
  /** ENABLED module keys. Network scope = union over granted schools; PARENT = union over own + children's schools. */
  modules: string[];
  states?: ModuleStateWire[];
  core?: string[];
  version?: string;
  /** Only for a PARENT with children in more than one school. */
  schools?: { schoolId: string; modules: string[] }[];
}

export type ModuleVisibility = "visible" | "locked" | "hidden";

export interface ModuleAccess {
  /** False until a response (or a remembered one) is in hand. */
  ready: boolean;
  scope: "school" | "network" | "none" | null;
  /** Hash of the enabled set; changes iff the set changes. */
  version: string | null;
  /**
   * Is `key` on? `true` for null/undefined/core keys and while not ready.
   * Pass `schoolId` to ask about one child's school (parents with children in
   * two schools); an unknown school falls back to the union.
   */
  hasModule: (key: string | null | undefined, schoolId?: string | null) => boolean;
  /** Raw state for a key: never throws, unknown keys are `{enabled:true, teaser:false}`. */
  moduleState: (key: string | null | undefined) => ModuleStateWire;
  /** visible / locked / hidden for a module key. */
  visibility: (key: string | null | undefined, schoolId?: string | null) => ModuleVisibility;
  /** Same, resolved from a web path or href (`/transport/routes?tab=x`). Core paths are "visible". */
  pathVisibility: (pathOrHref: string | null | undefined) => ModuleVisibility;
  /** Convenience: `pathVisibility(path) === "visible"`. Use for anything that fetches data or acts. */
  hasPath: (pathOrHref: string | null | undefined) => boolean;
}

const OPEN: ModuleStateWire = { key: "", enabled: true, teaser: false };

function stripToPath(hrefOrPath: string): string {
  return hrefOrPath.split(/[?#]/)[0] ?? hrefOrPath;
}

/**
 * Build the access object from an API response. `undefined` (loading, failed,
 * signed out) yields the fully-open object.
 */
export function createModuleAccess(
  data: EntitlementsResponse | null | undefined,
  opts: {
    /**
     * A PARENT with children in more than one school: answer every question
     * for THIS school (the active child's) unless the caller names another.
     * Ignored when the response carries no per-school list.
     */
    defaultSchoolId?: string | null;
  } = {},
): ModuleAccess {
  if (!data || !Array.isArray(data.modules)) {
    return {
      ready: false,
      scope: null,
      version: null,
      hasModule: () => true,
      moduleState: (key) => ({ ...OPEN, key: key ?? "" }),
      visibility: () => "visible",
      pathVisibility: () => "visible",
      hasPath: () => true,
    };
  }

  const enabledUnion = new Set(data.modules);
  const stateByKey = new Map<string, ModuleStateWire>();
  for (const s of data.states ?? []) stateByKey.set(s.key, s);
  // Older API: only `modules`. Anything we cannot find in `states` is judged
  // by membership of `modules` and is never a teaser.
  const known = stateByKey.size > 0;
  const perSchool = new Map<string, Set<string>>();
  for (const s of data.schools ?? []) perSchool.set(s.schoolId, new Set(s.modules));

  const moduleState = (key: string | null | undefined): ModuleStateWire => {
    if (!key) return { ...OPEN };
    const hit = stateByKey.get(key);
    if (hit) return hit;
    // Not in the catalogue (core, or a key this client predates): only a
    // key we were told about can be off.
    if (known) return { key, enabled: true, teaser: false };
    return { key, enabled: enabledUnion.has(key), teaser: false };
  };

  const scoped = opts.defaultSchoolId && perSchool.has(opts.defaultSchoolId) ? opts.defaultSchoolId : null;

  const hasModule = (key: string | null | undefined, schoolIdArg?: string | null): boolean => {
    if (!key) return true;
    const schoolId = schoolIdArg ?? scoped;
    if (schoolId) {
      const own = perSchool.get(schoolId);
      if (own) return known && !stateByKey.has(key) ? true : own.has(key);
    }
    return moduleState(key).enabled;
  };

  const visibility = (key: string | null | undefined, schoolIdArg?: string | null): ModuleVisibility => {
    if (hasModule(key, schoolIdArg)) return "visible";
    // A child's-school lookup is a parent; parents never get the teaser.
    const schoolId = schoolIdArg ?? scoped;
    if (schoolId && perSchool.has(schoolId)) return "hidden";
    return moduleState(key).teaser ? "locked" : "hidden";
  };

  const pathVisibility = (pathOrHref: string | null | undefined): ModuleVisibility => {
    if (!pathOrHref) return "visible";
    return visibility(moduleKeyForWebPath(stripToPath(pathOrHref)));
  };

  return {
    ready: true,
    scope: data.scope ?? null,
    version: data.version ?? null,
    hasModule,
    moduleState,
    visibility,
    pathVisibility,
    hasPath: (p) => pathVisibility(p) === "visible",
  };
}

/** The fully-open access object (loading / signed out). */
export const OPEN_MODULE_ACCESS: ModuleAccess = createModuleAccess(undefined);

/**
 * Launch-readiness AND per-school access, for callers that used to test
 * `isLaunchReadyPath(path)` alone to decide whether to render a card, link or
 * action. A path is shown only if the product has launched it and the school
 * has it on. A teaser ("locked") path is NOT shown here: this is for things
 * that fetch or act. Nav surfaces that render locked rows use `pathVisibility`.
 */
export function isPathShown(access: ModuleAccess, pathOrHref: string | null | undefined): boolean {
  if (!pathOrHref) return true;
  return isLaunchReadyPath(stripToPath(pathOrHref)) && access.hasPath(pathOrHref);
}
